import { supabase } from "@/integrations/supabase/client";

export interface Site {
  id: string;
  name: string;
  address: string;
  city: string;
  postal_code: string;
  phone?: string;
  email?: string;
  description?: string;
  services?: string[];
  opening_hours?: any;
  latitude?: number;
  longitude?: number;
  website?: string;
}

/**
 * Service pour récupérer les données des sites depuis Supabase
 */
export class SitesService {
  /**
   * Récupère tous les sites
   */
  static async getAllSites(): Promise<Site[]> {
    const { data, error } = await supabase
      .from('sites')
      .select('*')
      .order('name');

    if (error) {
      console.error('Erreur lors de la récupération des sites:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Récupère un site par son ID
   */
  static async getSiteById(id: string): Promise<Site | null> {
    const { data, error } = await supabase
      .from('sites')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('Erreur lors de la récupération du site:', error);
      return null;
    }

    return data;
  }

  /**
   * Récupère un site par son nom et sa ville (pour les URLs SEO)
   */
  static async getSiteByNameAndCity(siteName: string, city: string): Promise<Site | null> {
    const normalizedSiteName = siteName.replace(/-/g, ' ').toLowerCase();
    const normalizedCity = city.replace(/-/g, ' ').toLowerCase();

    const { data, error } = await supabase
      .from('sites')
      .select('*');

    if (error) {
      console.error('Erreur lors de la récupération du site:', error);
      return null;
    }

    // Recherche par nom et ville (approximative)
    const site = data?.find(s => 
      s.name.toLowerCase().includes(normalizedSiteName) ||
      normalizedSiteName.includes(s.name.toLowerCase()) ||
      (s.city.toLowerCase().replace(/[^a-z0-9]/g, '') === normalizedCity.replace(/[^a-z0-9]/g, ''))
    );

    return site || null;
  }

  /**
   * Génère l'URL SEO-friendly pour un site
   */
  static generateSiteUrl(site: Site): string {
    const siteSlug = site.name
      .toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ç]/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    
    const citySlug = site.city
      .toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ç]/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    
    return `/site/${siteSlug}/${citySlug}`;
  }

  /**
   * Récupère le mapping de tous les sites avec leurs URLs
   */
  static async getSiteUrlMapping(): Promise<Record<string, string>> {
    const sites = await this.getAllSites();
    const mapping: Record<string, string> = {};

    sites.forEach(site => {
      mapping[site.id] = this.generateSiteUrl(site);
      // Ajouter aussi le mapping par nom de site
      const siteSlug = site.name
        .toLowerCase()
        .replace(/[àáâãäå]/g, 'a')
        .replace(/[èéêë]/g, 'e')
        .replace(/[ìíîï]/g, 'i')
        .replace(/[òóôõö]/g, 'o')
        .replace(/[ùúûü]/g, 'u')
        .replace(/[ç]/g, 'c')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      mapping[siteSlug] = this.generateSiteUrl(site);
    });

    return mapping;
  }
}